/**
 * EN/VI language context. Persists to localStorage key "lang".
 * useT() → t(key) returns translated string or the key itself as fallback.
 * useLang() → { locale, setLocale } for reading/switching locale.
 *
 * `vi` is the default for nearly everyone and ships in the entry; `en` (≈90 KB gzipped) is loaded
 * on demand. A switch to English waits for the dictionary before it takes effect, so the tree is
 * never unmounted mid-edit and raw keys are never shown; only a stored English preference renders a
 * placeholder while the dictionary loads at start-up. If the chunk fails to load the app stays (or
 * falls back to) Vietnamese and the next switch retries.
 */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { vi } from './vi'

type Locale = 'vi' | 'en'
type Dict = Record<string, string>

interface LangCtxValue {
  locale: Locale
  setLocale: (l: Locale) => void
}

const LangCtx = createContext<LangCtxValue>({ locale: 'vi', setLocale: () => {} })
const DictCtx = createContext<Dict>(vi)

let enPromise: Promise<Dict> | undefined
function loadEn(): Promise<Dict> {
  enPromise ??= import('./en').then(
    (m) => m.en,
    (err: unknown) => {
      enPromise = undefined // a failed fetch is not cached: the next switch tries again
      throw err
    },
  )
  return enPromise
}

function persist(l: Locale): void {
  try { localStorage.setItem('lang', l) } catch { /* ignore */ }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      const stored = localStorage.getItem('lang')
      return stored === 'en' ? 'en' : 'vi'
    } catch {
      return 'vi'
    }
  })
  const [en, setEn] = useState<Dict | null>(null)
  // The last call wins: a switch back to vi while en is still loading must not be undone later.
  const request = useRef(0)

  // Stored English preference: load before rendering anything, fall back to vi if it cannot load.
  useEffect(() => {
    if (locale !== 'en' || en) return
    let cancelled = false
    loadEn().then(
      (dict) => { if (!cancelled) setEn(dict) },
      (err: unknown) => {
        if (cancelled) return
        console.error('English dictionary failed to load, staying in Vietnamese', err)
        setLocaleState('vi')
      },
    )
    return () => { cancelled = true }
  }, [locale, en])

  async function setLocale(l: Locale): Promise<void> {
    const id = ++request.current
    if (l === 'en' && !en) {
      let dict: Dict
      try {
        dict = await loadEn()
      } catch (err) {
        console.error('English dictionary failed to load, staying in Vietnamese', err)
        return
      }
      if (id !== request.current) return
      setEn(dict)
    }
    setLocaleState(l)
    persist(l)
  }

  const dict = locale === 'en' ? en : vi
  return (
    <LangCtx.Provider value={{ locale, setLocale }}>
      {dict ? (
        <DictCtx.Provider value={dict}>{children}</DictCtx.Provider>
      ) : (
        <div className="flex h-32 items-center justify-center text-sm text-muted">Loading…</div>
      )}
    </LangCtx.Provider>
  )
}

export function useLang(): LangCtxValue {
  return useContext(LangCtx)
}

/** Returns a t(key) lookup function bound to the current locale. */
export function useT(): (key: string) => string {
  const dict = useContext(DictCtx)
  return (key: string): string => dict[key] ?? key
}
